'use client'
import React from "react"
import { cn } from "@/lib/utils"

const CardContext = React.createContext({ hasHeader: false })

const Card = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, children, ...props }, ref) => {
    const hasHeader = React.Children.toArray(children).some(
      (child) => React.isValidElement(child) && (child.type as { displayName?: string }).displayName === "CardHeader"
    )
    return (
      <CardContext.Provider value={{ hasHeader }}>
        <div ref={ref} className={cn("rounded-[12px] border border-border-light bg-background text-card-foreground", className)} {...props}>
          {children}
        </div>
      </CardContext.Provider>
    )
  }
)
Card.displayName = "Card"

const CardHeader = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn("px-3 md:px-5 pt-3 md:pt-5 pb-2.5 bg-background sticky top-0 border-b border-border-light", className)} {...props} />
  )
)
CardHeader.displayName = "CardHeader"

const CardTitle = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLHeadingElement>>(
  ({ className, ...props }, ref) => (
    <h3 ref={ref} className={cn("", className)} {...props} />
  )
)
CardTitle.displayName = "CardTitle"

const CardDescription = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLParagraphElement>>(
  ({ className, ...props }, ref) => (
    <p ref={ref} className={cn("text-sm text-muted-foreground", className)} {...props} />
  )
)
CardDescription.displayName = "CardDescription"

const CardContent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => {
    const { hasHeader } = React.useContext(CardContext)
    return (
      <div
        ref={ref}
        className={cn(
          hasHeader ? "px-3 md:px-5 pb-3 md:pb-5 pt-2.5" : "p-3 md:p-5",
          className
        )}
        {...props}
      />
    )
  }
)
CardContent.displayName = "CardContent"

const CardFooter = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn("flex items-center p-6 pt-0", className)} {...props} />
  )
)
CardFooter.displayName = "CardFooter"

export { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter }
